import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmTabState } from 'common/hooks/usePalmTabState';
import { fetchUserGroups } from 'common/services/api/UserGroups/UserGroups.thunk';
import { ActionDefinition, ColumnDefinition, ColumnType, Operations } from 'common/types/TypeDefs';

import { UserGroupCreateContent } from './UserGroupsCreateDrawerContent';
import { UserGroupsDeleteDialogContent } from './UserGroupsDeleteDialogContent';
import { addNewFilter, clearFilters, removeFilter, selectFilters } from './userGroupsSlice';

export function UserGroupsTab() {
    const typeName = 'User Group';
    const { tenantId = '', suiteId } = useParams();

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.MultiTenantRO, Roles.UserManager, Roles.AppManager ]);

    const { processing, sortModel, setSortModel, filterProp, fetchEntities } = usePalmTabState({
        typeName,
        fetchParams: { tenantId },
        fetchThunk: fetchUserGroups,
        filterActions: { selectFilters, addNewFilter, removeFilter, clearFilters }
    });

    const onGetRowLink = useCallback((id: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.UserGroups}/${id}/${Routes.Users}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: onGetRowLink },
        { action: Operations.Edit, disabled: shouldDisableButtons },
        { action: Operations.Delete, disabled: shouldDisableButtons }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'id', columnName: 'User Group ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'name', columnName: 'User Group Name', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'description', columnName: 'User Group Description', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        contentFunction={UserGroupCreateContent}
        deleteFunction={UserGroupsDeleteDialogContent}
        title={typeName}
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        isCreateButtonDisabled={shouldDisableButtons}
        getRowLink={shouldDisableOpenButton ? undefined : onGetRowLink}
        filter={filterProp}
        refreshDataGridClick={() => fetchEntities()}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
